import { escapeHtml } from "../html.ts";
import {
  createMission,
  deleteMission,
  getMission,
  getTemplate,
  listTemplatesBBox,
  listUserPieces,
  publishMission,
  updateMission,
} from "../api.ts";
import { applyStaticCopy, formatDistance, t } from "../i18n/index.ts";
import { appendLanguageSwitch, languageSelect } from "../i18n/switcher.ts";
import { mountLegalFooter } from "../legal-footer.ts";
import type { AuthUser } from "../../shared/types.ts";
import type { LngLat } from "../../shared/types.ts";
import {
  DEFAULT_SPOT_RADIUS_M,
  questDistanceM,
  sumEstMinutes,
} from "../../shared/mission-quest.ts";
import { QuestMap } from "./quest-map.ts";
import { questEditPath, questViewPath, type QuestRoute } from "./router.ts";
import { emptyQuestDraft, toWriteBody, type QuestDetail, type QuestSpot } from "./types.ts";

export interface QuestPageOptions {
  route: QuestRoute;
  user: AuthUser | null;
  root: HTMLElement;
  onRequireSignIn: () => void;
  onToast: (message: string) => void;
}

/**
 * Unified quest page: empty = intro template; edit fills it in place;
 * view mode is the public keepsake layout (hidden unlock fields omitted by API).
 */
export class QuestPage {
  private opts: QuestPageOptions;
  private quest: QuestDetail;
  private mode: "edit" | "view";
  private map: QuestMap | null = null;
  private line: LngLat[] | null = null;
  private expandedSpot = -1;
  private busy = false;

  constructor(opts: QuestPageOptions) {
    this.opts = opts;
    this.quest = emptyQuestDraft();
    this.mode = opts.route.kind === "view" ? "view" : "edit";
  }

  async mount(): Promise<void> {
    const { route, user } = this.opts;
    if ((route.kind === "new" || route.kind === "edit") && !user) {
      this.opts.onRequireSignIn();
      this.opts.root.innerHTML = `<div class="quest-shell"><p class="quest-msg" data-i18n="quest.signInRequired">Sign in to create a quest.</p><a class="btn" href="/">${t("quest.backHome")}</a></div>`;
      applyStaticCopy(this.opts.root);
      appendLanguageSwitch(this.opts.root);
      mountLegalFooter(this.opts.root.querySelector(".quest-shell") ?? this.opts.root);
      return;
    }
    if (route.kind === "new") {
      this.quest = emptyQuestDraft();
      this.quest.author = user?.name ?? null;
      await this.ensureSaved();
    } else {
      this.quest = await getMission(route.id);
      if (route.kind === "edit" && this.quest.status !== "draft") {
        history.replaceState(null, "", questViewPath(this.quest.id));
        this.mode = "view";
      }
    }
    if (this.quest.template_id) await this.loadLine(this.quest.template_id);
    this.render();
  }

  destroy(): void {
    this.map?.destroy();
    this.map = null;
  }

  private async ensureSaved(): Promise<void> {
    if (this.quest.id) return;
    const created = await createMission(toWriteBody(this.quest));
    this.quest = created;
    history.replaceState(null, "", questEditPath(created.id));
  }

  private async loadLine(templateId: string): Promise<void> {
    try {
      const detail = await getTemplate(templateId);
      const coords = detail.geometry?.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        this.line = coords.map((p) => [p[0], p[1]] as LngLat);
      } else this.line = null;
    } catch {
      this.line = null;
    }
  }

  private render(): void {
    const edit = this.mode === "edit" && this.quest.status === "draft";
    const root = this.opts.root;
    root.innerHTML = "";
    const shell = el("div", "quest-shell");
    shell.append(this.renderToolbar(edit));
    const page = el("article", "quest-page");
    page.append(this.renderCover(edit));
    page.append(this.renderIntro(edit));
    page.append(this.renderSummary());
    page.append(this.renderMapBlock(edit));
    page.append(this.renderSpots(edit));
    page.append(this.renderTextSection("story", t("quest.story"), this.quest.story, edit, (v) => {
      this.quest.story = v;
    }));
    page.append(this.renderTextSection("manners", t("quest.manners"), this.quest.manners, edit, (v) => {
      this.quest.manners = v;
    }));
    page.append(this.renderPhase2());
    shell.append(page);
    mountLegalFooter(shell);
    root.append(shell);
    applyStaticCopy(root);
    this.mountMap(edit);
  }

  private renderToolbar(edit: boolean): HTMLElement {
    const bar = el("div", "quest-toolbar glass-panel");
    const back = el("a", "text-btn") as HTMLAnchorElement;
    back.href = "/";
    back.textContent = t("quest.backHome");
    bar.append(back);
    const status = el("span", "quest-status");
    status.textContent =
      this.quest.status === "draft" ? t("quest.status.draft") : t("quest.status.published");
    bar.append(status);
    if (edit) {
      bar.append(btn(t("quest.saveDraft"), "btn", () => void this.save()));
      bar.append(btn(t("quest.preview"), "btn", () => {
        this.mode = "view";
        this.render();
      }));
      bar.append(btn(t("quest.publish"), "btn btn-primary", () => void this.publish()));
      bar.append(btn(t("quest.deleteDraft"), "btn-danger", () => void this.deleteDraft()));
    } else if (this.quest.status === "draft" && this.quest.can_edit) {
      bar.append(btn(t("quest.edit"), "btn btn-primary", () => {
        this.mode = "edit";
        history.replaceState(null, "", questEditPath(this.quest.id));
        this.render();
      }));
    } else if (this.quest.can_delete) {
      bar.append(btn(t("quest.delete"), "btn-danger", () => void this.deleteDraft()));
    }
    bar.append(languageSelect());
    return bar;
  }

  /** Before the page reloads in another language: keep what the creator typed. */
  async flushPending(): Promise<boolean> {
    if (this.mode !== "edit" || this.quest.status !== "draft" || !this.quest.id) return true;
    return this.save();
  }

  private renderCover(edit: boolean): HTMLElement {
    const wrap = el("div", "quest-cover");
    if (this.quest.cover_url) {
      const img = document.createElement("img");
      img.src = this.quest.cover_url;
      img.alt = "";
      img.className = "quest-cover-img";
      wrap.append(img);
    } else {
      const ph = el("div", "quest-cover-placeholder");
      ph.textContent = t("quest.cover.placeholder");
      wrap.append(ph);
    }
    if (edit) {
      const input = document.createElement("input");
      input.type = "url";
      input.className = "quest-inline-input";
      input.placeholder = t("quest.cover.url");
      input.value = this.quest.cover_url ?? "";
      input.addEventListener("change", () => {
        this.quest.cover_url = input.value.trim() || null;
        this.render();
      });
      wrap.append(input);
    }
    return wrap;
  }

  private renderIntro(edit: boolean): HTMLElement {
    const intro = el("header", "quest-intro");
    intro.append(this.field(edit, "h1", "quest-title", this.quest.title, t("quest.title.placeholder"), (v) => {
      this.quest.title = v;
    }));
    intro.append(this.field(edit, "p", "quest-tagline", this.quest.tagline ?? "", t("quest.tagline.placeholder"), (v) => {
      this.quest.tagline = v || null;
    }));
    const place = el("p", "quest-place");
    if (edit) {
      const city = document.createElement("input");
      city.className = "quest-inline-input";
      city.placeholder = t("quest.city");
      city.value = this.quest.city ?? "";
      city.addEventListener("change", () => {
        this.quest.city = city.value.trim() || null;
      });
      const country = document.createElement("input");
      country.className = "quest-inline-input";
      country.placeholder = t("quest.country");
      country.value = this.quest.country ?? "";
      country.addEventListener("change", () => {
        this.quest.country = country.value.trim() || null;
      });
      place.append(city, document.createTextNode(" · "), country);
    } else {
      const parts = [this.quest.city, this.quest.country].filter(Boolean);
      place.textContent = parts.join(", ") || "—";
    }
    intro.append(place);

    const tags = el("div", "quest-tags");
    for (const tag of this.quest.tags) {
      const chip = el("span", "quest-tag");
      chip.textContent = tag;
      tags.append(chip);
    }
    if (edit) {
      const tagInput = document.createElement("input");
      tagInput.className = "quest-inline-input";
      tagInput.placeholder = t("quest.tags.placeholder");
      tagInput.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const v = tagInput.value.trim();
        if (!v || this.quest.tags.includes(v)) return;
        this.quest.tags = [...this.quest.tags, v].slice(0, 12);
        tagInput.value = "";
        this.render();
      });
      tags.append(tagInput);
    }
    intro.append(tags);

    const creator = el("p", "quest-creator");
    creator.textContent = this.quest.author ? t("quest.by", { name: this.quest.author }) : t("quest.byUnknown");
    intro.append(creator);
    return intro;
  }

  private renderSummary(): HTMLElement {
    const row = el("div", "quest-summary");
    const dist = questDistanceM(this.line, this.quest.checkpoints);
    const mins = sumEstMinutes(this.quest.checkpoints);
    row.append(stat(t("quest.summary.distance"), formatDistance(dist)));
    row.append(stat(t("quest.summary.time"), t("eta.minutes", { count: mins })));
    row.append(stat(t("quest.summary.spots"), String(this.quest.checkpoints.length)));
    return row;
  }

  private renderMapBlock(edit: boolean): HTMLElement {
    const block = el("section", "quest-map-block");
    if (edit) {
      const picks = el("div", "quest-line-picks");
      picks.append(btn(t("quest.line.none"), "text-btn", () => void this.setLineChoice(null)));
      picks.append(btn(t("quest.line.pickMine"), "text-btn", () => void this.pickMyLine()));
      picks.append(btn(t("quest.line.pickNearby"), "text-btn", () => void this.pickNearbyLine()));
      const hint = el("p", "quest-map-hint");
      hint.textContent = t("quest.map.hint");
      block.append(picks, hint);
    }
    const mapEl = el("div", "quest-map");
    mapEl.id = "quest-map";
    block.append(mapEl);
    return block;
  }

  private mountMap(edit: boolean): void {
    const mapEl = this.opts.root.querySelector("#quest-map") as HTMLElement | null;
    if (!mapEl) return;
    this.map?.destroy();
    this.map = new QuestMap(mapEl, edit, {
      onAddSpot: (lat, lng) => {
        this.addSpot(lat, lng);
      },
      onMoveSpot: (index, lat, lng) => {
        const spot = this.quest.checkpoints[index];
        if (!spot) return;
        spot.lat = lat;
        spot.lng = lng;
        this.syncMap();
      },
    });
    this.syncMap();
  }

  private syncMap(): void {
    this.map?.setLine(this.line);
    this.map?.setSpots(this.quest.checkpoints);
  }

  private renderSpots(edit: boolean): HTMLElement {
    const section = el("section", "quest-spots");
    const h = el("h2", "quest-section-title");
    h.textContent = t("quest.spots");
    section.append(h);
    if (!this.quest.checkpoints.length) {
      const empty = el("p", "quest-msg");
      empty.textContent = edit ? t("quest.spots.emptyEdit") : t("quest.spots.empty");
      section.append(empty);
      return section;
    }
    this.quest.checkpoints.forEach((spot, index) => {
      section.append(this.renderSpotCard(spot, index, edit));
    });
    return section;
  }

  private renderSpotCard(spot: QuestSpot, index: number, edit: boolean): HTMLElement {
    const card = el("div", "quest-spot");
    const head = el("button", "quest-spot-head");
    (head as HTMLButtonElement).type = "button";
    head.innerHTML = `<span class="quest-spot-num">${index + 1}</span>
      <span class="quest-spot-title">${escapeHtml(spot.title || t("quest.spot.untitled"))}</span>
      <span class="quest-spot-meta">${escapeHtml(spot.label || placeLabel(spot))} · ~${spot.est_minutes ?? "—"} ${t("quest.min")}</span>
      <span class="quest-slot quest-slot-stamp" title="${escapeHtml(t("quest.slot.stamp"))}"></span>
      <span class="quest-slot quest-slot-photo" title="${escapeHtml(t("quest.slot.photo"))}"></span>`;
    if (edit) {
      head.addEventListener("click", () => {
        this.expandedSpot = this.expandedSpot === index ? -1 : index;
        this.render();
      });
    }
    card.append(head);
    if (edit && this.expandedSpot === index) {
      card.append(this.renderSpotEditor(spot, index));
    }
    return card;
  }

  private renderSpotEditor(spot: QuestSpot, index: number): HTMLElement {
    const form = el("div", "quest-spot-edit");
    form.append(labeledInput(t("quest.spot.title"), spot.title ?? "", (v) => {
      spot.title = v || null;
    }));
    form.append(labeledInput(t("quest.spot.minutes"), String(spot.est_minutes ?? ""), (v) => {
      const n = Number(v);
      spot.est_minutes = Number.isInteger(n) && n > 0 ? n : null;
    }, "number"));
    form.append(labeledInput(t("quest.spot.radius"), String(spot.radius_m), (v) => {
      const n = Number(v);
      if (Number.isFinite(n)) spot.radius_m = n;
    }, "number"));
    const photo = document.createElement("label");
    photo.className = "quest-check";
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = spot.needs_media;
    cb.addEventListener("change", () => {
      spot.needs_media = cb.checked;
    });
    photo.append(cb, document.createTextNode(` ${t("quest.spot.photoRequired")}`));
    form.append(photo);

    const lock = el("div", "quest-unlock");
    const lockTitle = el("p", "quest-unlock-title");
    lockTitle.textContent = `🔒 ${t("quest.unlock.title")}`;
    lock.append(lockTitle);
    lock.append(labeledTextarea(t("quest.unlock.story"), spot.hidden_story ?? "", (v) => {
      spot.hidden_story = v || null;
    }));
    lock.append(labeledTextarea(t("quest.unlock.pose"), spot.pose_hint ?? "", (v) => {
      spot.pose_hint = v || null;
    }));
    lock.append(labeledInput(t("quest.unlock.link"), spot.link_url ?? "", (v) => {
      spot.link_url = v || null;
    }, "url"));
    form.append(lock);

    if (spot.kind === "spot" || spot.kind == null) {
      form.append(btn(t("quest.spot.remove"), "text-btn", () => {
        this.quest.checkpoints = this.quest.checkpoints.filter((_, i) => i !== index);
        this.expandedSpot = -1;
        this.render();
      }));
    }
    return form;
  }

  private renderTextSection(
    key: string,
    title: string,
    value: string | null,
    edit: boolean,
    onChange: (v: string | null) => void,
  ): HTMLElement {
    const section = el("section", "quest-text-section");
    section.dataset.section = key;
    const h = el("h2", "quest-section-title");
    h.textContent = title;
    section.append(h);
    if (edit) {
      const ta = document.createElement("textarea");
      ta.className = "quest-textarea";
      ta.placeholder = t("quest.text.placeholder");
      ta.value = value ?? "";
      ta.addEventListener("change", () => onChange(ta.value.trim() || null));
      section.append(ta);
    } else {
      const p = el("p", "quest-text");
      p.textContent = value?.trim() ? value : t("quest.text.empty");
      section.append(p);
    }
    return section;
  }

  private renderPhase2(): HTMLElement {
    const section = el("section", "quest-phase2");
    const h = el("h2", "quest-section-title");
    h.textContent = t("quest.more.title");
    const p = el("p", "quest-msg");
    p.textContent = t("quest.more.placeholder");
    section.append(h, p);
    return section;
  }

  private field(
    edit: boolean,
    tag: string,
    className: string,
    value: string,
    placeholder: string,
    onChange: (v: string) => void,
  ): HTMLElement {
    if (!edit) {
      const node = el(tag, className);
      node.textContent = value || "—";
      return node;
    }
    const input = document.createElement(tag === "h1" ? "input" : "input");
    input.className = `${className} quest-inline-input`;
    input.placeholder = placeholder;
    input.value = value;
    input.addEventListener("change", () => onChange(input.value.trim()));
    return input;
  }

  private addSpot(lat: number, lng: number): void {
    const spots = [...this.quest.checkpoints];
    const spot: QuestSpot = {
      lat,
      lng,
      radius_m: DEFAULT_SPOT_RADIUS_M,
      label: null,
      needs_media: false,
      title: null,
      est_minutes: 15,
      hidden_story: null,
      pose_hint: null,
      link_url: null,
      kind: "spot",
    };
    if (spots.length === 0) {
      spots.push({ ...spot, kind: "start", title: t("quest.spot.start"), est_minutes: 10 });
    } else if (spots.length === 1 && spots[0]!.kind === "start") {
      spots.push({ ...spot, kind: "finish", title: t("quest.spot.finish") });
    } else {
      // Insert before finish when present
      const finishIdx = spots.findIndex((s) => s.kind === "finish");
      if (finishIdx >= 0) spots.splice(finishIdx, 0, spot);
      else spots.push(spot);
    }
    this.quest.checkpoints = spots;
    this.render();
  }

  private async setLineChoice(templateId: string | null): Promise<void> {
    this.quest.template_id = templateId;
    this.quest.preset = templateId ? "walk_piece" : null;
    if (templateId) await this.loadLine(templateId);
    else this.line = null;
    this.render();
  }

  private async pickMyLine(): Promise<void> {
    try {
      const { pieces } = await listUserPieces();
      const published = pieces.filter((p) => p.published && p.template_id);
      if (!published.length) {
        this.opts.onToast(t("quest.line.noneMine"));
        return;
      }
      const name = window.prompt(
        t("quest.line.pickPrompt") + "\n" + published.map((p, i) => `${i + 1}. ${p.name}`).join("\n"),
      );
      const idx = Number(name) - 1;
      const chosen = published[idx];
      if (!chosen?.template_id) return;
      await this.setLineChoice(chosen.template_id);
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  }

  private async pickNearbyLine(): Promise<void> {
    try {
      const { templates } = await listTemplatesBBox({
        minLat: this.quest.center_lat - 0.05,
        minLng: this.quest.center_lng - 0.05,
        maxLat: this.quest.center_lat + 0.05,
        maxLng: this.quest.center_lng + 0.05,
      }, 0);
      if (!templates.length) {
        this.opts.onToast(t("quest.line.noneNearby"));
        return;
      }
      const pick = window.prompt(
        t("quest.line.pickPrompt") + "\n" + templates.slice(0, 12).map((p, i) => `${i + 1}. ${p.title}`).join("\n"),
      );
      const idx = Number(pick) - 1;
      const chosen = templates[idx];
      if (!chosen) return;
      await this.setLineChoice(chosen.id);
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  }

  private async save(): Promise<boolean> {
    if (this.busy) return false;
    this.busy = true;
    try {
      await this.ensureSaved();
      this.quest = await updateMission(this.quest.id, toWriteBody(this.quest));
      if (this.quest.template_id) await this.loadLine(this.quest.template_id);
      this.opts.onToast(t("quest.saved"));
      this.render();
      return true;
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
      return false;
    } finally {
      this.busy = false;
    }
  }

  private async publish(): Promise<void> {
    if (!window.confirm(t("quest.publishConfirm"))) return;
    try {
      if (!(await this.save())) return;
      this.quest = await publishMission(this.quest.id);
      this.mode = "view";
      history.replaceState(null, "", questViewPath(this.quest.id));
      this.opts.onToast(t("quest.published"));
      this.render();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.publishFailed"));
    }
  }

  private async deleteDraft(): Promise<void> {
    if (!this.quest.id) {
      location.href = "/";
      return;
    }
    if (!window.confirm(t("quest.deleteConfirm"))) return;
    try {
      await deleteMission(this.quest.id);
      location.href = "/";
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.deleteFailed"));
    }
  }
}

function el(tag: string, className?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function btn(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = className;
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

function stat(label: string, value: string): HTMLElement {
  const s = el("span", "quest-stat");
  s.innerHTML = `<span>${escapeHtml(label)}</span> <b>${escapeHtml(value)}</b>`;
  return s;
}

function labeledInput(
  label: string,
  value: string,
  onChange: (v: string) => void,
  type = "text",
): HTMLElement {
  const wrap = el("label", "quest-field");
  wrap.append(document.createTextNode(label));
  const input = document.createElement("input");
  input.type = type;
  input.value = value;
  input.className = "quest-inline-input";
  input.addEventListener("change", () => onChange(input.value.trim()));
  wrap.append(input);
  return wrap;
}

function labeledTextarea(label: string, value: string, onChange: (v: string) => void): HTMLElement {
  const wrap = el("label", "quest-field");
  wrap.append(document.createTextNode(label));
  const ta = document.createElement("textarea");
  ta.className = "quest-textarea";
  ta.value = value;
  ta.addEventListener("change", () => onChange(ta.value.trim()));
  wrap.append(ta);
  return wrap;
}

function placeLabel(spot: QuestSpot): string {
  return `${spot.lat.toFixed(4)}, ${spot.lng.toFixed(4)}`;
}
